import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { styleSheet } from './styles';

interface IDataGridProps extends React.TableHTMLAttributes<HTMLTableElement> {
	children: React.ReactNode;
	styles?: StyleDeclarationValue[];
	fullHeight?: boolean;
	isLoading?: boolean;
}

export const DataGrid: React.FC<IDataGridProps> = ({
	children,
	styles = [],
	fullHeight = false,
	isLoading = false,
	className,
	...tableProps
}) => {
	return (
		<table
			{...tableProps}
			className={css(
				styleSheet.table,
				isLoading && styleSheet.tableLoadingState,
				styles,
				fullHeight && styleSheet.fullHeight
			).concat(className ? ` ${className}` : '')}
		>
			{children}
		</table>
	);
};

interface IDataGridColumnProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
	children?: React.ReactNode;
	styles?: StyleDeclarationValue[];
	sticky?: boolean;
}

export const DataGridColumn: React.FC<IDataGridColumnProps> = ({
	children,
	styles = [],
	sticky = false,
	className,
	scope = 'col',
	...columnProps
}) => {
	const thRef = React.useRef<HTMLTableCellElement>(null);

	// Filter out null values before passing to css()
	const columnStyles = [styleSheet.headerCell, sticky && styleSheet.headerCellSticky, ...styles].filter(Boolean);

	return (
		<th
			{...columnProps}
			ref={thRef}
			className={css(...columnStyles).concat(className ? ` ${className}` : '')}
			scope={scope}
		>
			{children}
		</th>
	);
};

interface IDataGridColumnsProps extends React.HTMLAttributes<HTMLTableSectionElement> {
	children: React.ReactNode;
	styles?: StyleDeclarationValue[];
}

export const DataGridColumns: React.FC<IDataGridColumnsProps> = ({
	children,
	styles = [],
	className,
	...headerProps
}) => {
	return (
		<thead {...headerProps} className={css(styles).concat(className ? ` ${className}` : '')}>
			<tr className={css(styleSheet.headerRow, styleSheet.row)}>{children}</tr>
		</thead>
	);
};

interface IDataGridRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
	children: React.ReactNode;
	styles?: StyleDeclarationValue[];
}

export const DataGridRow: React.FC<IDataGridRowProps> = ({ children, styles = [], className, ...rowProps }) => {
	return (
		<tr {...rowProps} className={css(styleSheet.row, styles).concat(className ? ` ${className}` : '')}>
			{children}
		</tr>
	);
};

interface IDataGridRowItemProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
	children?: React.ReactNode;
	styles?: StyleDeclarationValue[];
	fullColSpan?: boolean;
	noTopBorder?: boolean;
}

export const DataGridRowItem: React.FC<IDataGridRowItemProps> = ({
	children,
	styles = [],
	colSpan,
	fullColSpan,
	noTopBorder,
	className,
	...cellProps
}) => {
	if (colSpan !== undefined && fullColSpan !== undefined) {
		console.warn('Both colSpan and fullColSpan are defined, relying on colSpan');
	}

	return (
		<td
			{...cellProps}
			className={css(noTopBorder ? styleSheet.cellBorderless : styleSheet.cell, styles).concat(
				className ? ` ${className}` : ''
			)}
			colSpan={colSpan ?? (fullColSpan ? 100 : undefined)}
		>
			{children}
		</td>
	);
};

interface IDataGridRowsProps extends React.HTMLAttributes<HTMLTableSectionElement> {
	children: React.ReactNode;
	styles?: StyleDeclarationValue[];
}

export const DataGridRows: React.FC<IDataGridRowsProps> = ({ children, styles = [], className, ...bodyProps }) => {
	return (
		<tbody {...bodyProps} className={css(styles).concat(className ? ` ${className}` : '')}>
			{children}
		</tbody>
	);
};
